<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "~/lib/validator/validators";

const { productIds } = defineProps<{
	productIds: string[];
}>();

const { apiClient } = useShopwareContext();

const {
	data: { elements: products }
} = await apiClient.invoke("readProduct post /product", {
	body: {
		ids: productIds,
		limit: 4,
		page: 1,
		includes: {
			product: ["id", "name", "cover"],
			product_media: ["media"],
			media: ["alt", "url"]
		}
	}
});

const isOpen = ref<boolean>(true);

const reviewPoints = reactive<any>({});

products?.forEach((product) => {
	reviewPoints[product.id] = "5";
});

const formValues = useState(() => ({
	title: "",
	content: "",
	name: "",
	email: ""
}));

const validator = useVuelidate(
	{
		title: { required },
		content: { required },
		name: { required },
		email: { required, email }
	},
	formValues
);

const isLoading = ref(false);

const onSubmit = async () => {
	try {
		isLoading.value = true;
		await validator.value.$validate();
		if (validator.value.$error || !products) {
			return;
		}

		await Promise.all(
			products.map((product: any) =>
				apiClient.invoke("saveProductReview post /product/{productId}/review", {
					pathParams: { productId: product.id },
					body: {
						...formValues.value,
						points: parseInt(reviewPoints[product.id] as string)
					}
				})
			) ?? []
		);

		isOpen.value = false;
		formValues.value = {
			title: "",
			content: "",
			name: "",
			email: ""
		};

		push.success({ message: `Review${products.length > 1 ? "(s)" : ""} zijn geplaatst` });
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};
</script>

<template>
	<ClientOnly>
		<KippieSidebar v-model="isOpen" color="white" placement="right">
			<template #top>
				<Typography variant="h6">Schrijf een review</Typography>
			</template>
			<div class="flex flex-col gap-6">
				<div
					v-for="(product, index) in products"
					:key="`review-${product.id}`"
					class="flex justify-between gap-6 items-center"
				>
					<div class="w-50 h-50 bg-yellow p-2 rounded shadow">
						<NuxtImg :src="product?.cover?.media?.url ?? ''" :alt="product?.name ?? ''" class="h-auto" />
					</div>
					<FormField :label="`${index + 1}. ${product?.name}`">
						<KippieRatingInput v-model="reviewPoints[product.id]" />
					</FormField>
				</div>

				<Divider />

				<KippieInput
					v-model="formValues.title"
					label="Titel van de review"
					required
					:error="validator.title.$error"
					:help="validator.title.$errors.map((e) => e.$message).join(', ')"
				/>

				<KippieInput
					type="textarea"
					v-model="formValues.content"
					label="Inhoud van de review"
					required
					:rows="3"
					:error="validator.content.$error"
					:help="validator.content.$errors.map((e) => e.$message).join(', ') ?? 'Voorbeeld: Heerlijke pan'"
				/>

				<KippieInput
					v-model="formValues.name"
					label="Jouw naam"
					required
					:error="validator.name.$error"
					:help="validator.name.$errors.map((e) => e.$message).join(', ')"
				/>

				<KippieInput
					type="email"
					v-model="formValues.email"
					label="E-mailadres"
					required
					:error="validator.email.$error"
					:help="validator.email.$errors.map((e) => e.$message).join(', ')"
				/>
			</div>

			<template #bottom>
				<div class="bg-sand flex gap-2 p-4 -m-5">
					<KippieButton block size="small" @click="onSubmit" :loading="isLoading">Opslaan</KippieButton>
				</div>
			</template>
		</KippieSidebar>
	</ClientOnly>
</template>
